import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, locationsAPI } from '../api';
import AdminLayout from '../components/AdminLayout';

/* ─── Toast ─── */
const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-5 right-5 z-[100] flex items-center gap-2.5 pl-4 pr-3 py-2.5 rounded-lg shadow-lg border text-[13px] font-medium
      ${type === 'success' ? 'bg-white border-gray-200 text-gray-900' : 'bg-red-50 border-red-200 text-red-700'}`}>
      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${type === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`} />
      {message}
      <button onClick={onClose} className="ml-2 text-gray-400 hover:text-gray-600">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </div>
  );
};

/* ─── Confirm ─── */
const ConfirmModal = ({ title, message, onConfirm, onCancel, danger }) => (
  <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 backdrop-blur-[2px] px-4">
    <div className="bg-white rounded-lg shadow-xl max-w-sm w-full overflow-hidden">
      <div className="p-5">
        <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
        <p className="text-[13px] text-gray-500 mt-1.5">{message}</p>
      </div>
      <div className="flex gap-2 justify-end px-5 py-3.5 bg-gray-50 border-t border-gray-200">
        <button onClick={onCancel} className="px-3 py-1.5 text-[13px] font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition shadow-sm">Cancel</button>
        <button onClick={onConfirm} className={`px-3 py-1.5 text-[13px] font-medium text-white rounded-md transition shadow-sm ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-900 hover:bg-gray-800'}`}>Confirm</button>
      </div>
    </div>
  </div>
);

/* ─── Skeleton ─── */
const Skeleton = ({ className }) => <div className={`animate-pulse bg-gray-100 rounded ${className}`} />;

const TableSkeleton = ({ rows = 5, cols = 4 }) => (
  <div>{Array.from({ length: rows }).map((_, r) => (
    <div key={r} className="flex gap-6 px-5 py-3.5 border-b border-gray-100 last:border-0">
      {Array.from({ length: cols }).map((_, c) => <Skeleton key={c} className={`h-3 ${c === 0 ? 'w-32' : 'w-20'}`} />)}
    </div>
  ))}</div>
);

/* ─── Empty ─── */
const EmptyState = ({ title, subtitle }) => (
  <div className="text-center py-14 px-4">
    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m16.5 0V6.75a2.25 2.25 0 00-2.25-2.25H5.75a2.25 2.25 0 00-2.25 2.25V7.5m16.5 0h-16.5" /></svg>
    </div>
    <p className="text-[13px] font-medium text-gray-900">{title}</p>
    <p className="text-[12px] text-gray-400 mt-0.5">{subtitle}</p>
  </div>
);

/* ─── Badge ─── */
const Dot = ({ color }) => <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${color}`} />;

const StatusBadge = ({ status }) => {
  const map = {
    active: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
    inactive: { dot: 'bg-gray-300', text: 'text-gray-500' },
    completed: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
    pending: { dot: 'bg-amber-400', text: 'text-amber-700' },
    cancelled: { dot: 'bg-red-400', text: 'text-red-600' },
    delivered: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
    confirmed: { dot: 'bg-blue-500', text: 'text-blue-700' },
    resolved: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
    dismissed: { dot: 'bg-gray-300', text: 'text-gray-500' },
    reviewed: { dot: 'bg-blue-500', text: 'text-blue-700' },
  };
  const s = map[status] || { dot: 'bg-gray-300', text: 'text-gray-500' };
  return <span className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${s.text}`}><Dot color={s.dot} />{status}</span>;
};

/* ─── Table Wrapper ─── */
const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${className}`}>{children}</div>
);

const TH = ({ children }) => (
  <th className="text-left px-5 py-2.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">{children}</th>
);

const TD = ({ children, className = '' }) => (
  <td className={`px-5 py-3 text-[13px] ${className}`}>{children}</td>
);

/* ═══════════════════════════════════════
   MAIN
   ═══════════════════════════════════════ */
const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportStats, setReportStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subStats, setSubStats] = useState(null);
  const [pendingListings, setPendingListings] = useState([]);
  const [locProvinces, setLocProvinces] = useState([]);
  const [locDistricts, setLocDistricts] = useState([]);
  const [locSectors, setLocSectors] = useState([]);
  const [locExpanded, setLocExpanded] = useState({});
  const [locForm, setLocForm] = useState({ type: '', parent_id: '', name: '', editing: null });
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [settings, setSettings] = useState({});
  const [settingsMeta, setSettingsMeta] = useState({});
  const [settingsTab, setSettingsTab] = useState('general');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [brandingUploading, setBrandingUploading] = useState(false);
  const [adsList, setAdsList] = useState([]);
  const [adsStats, setAdsStats] = useState(null);
  const [adsPlacements, setAdsPlacements] = useState([]);
  const [adsSizes, setAdsSizes] = useState({});
  const [adsForm, setAdsForm] = useState(null);
  const [adsUploading, setAdsUploading] = useState(false);
  const [planConfig, setPlanConfig] = useState(null);
  const [plansSaving, setPlansSaving] = useState(false);

  const showToast = useCallback((msg, type = 'success') => setToast({ message: msg, type }), []);

  useEffect(() => {
    Promise.all([adminAPI.getDashboard(), adminAPI.getPendingListings()])
      .then(([d, p]) => {
        setStats(d.data);
        const l = p.data.listings || [];
        setPendingListings(l);
        setPendingCount(l.length);
      }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === 'overview') return;
    setTabLoading(true); setSearch('');
    (async () => {
      try {
        if (activeTab === 'users') { const r = await adminAPI.getUsers(); setUsers(r.data.users || []); }
        else if (activeTab === 'bookings') { const r = await adminAPI.getBookings(); setBookings(r.data.bookings || []); }
        else if (activeTab === 'payments') { const r = await adminAPI.getPayments(); setPayments(r.data.payments || []); }
        else if (activeTab === 'services') { const r = await adminAPI.getServices(); setServices(r.data.services || []); }
        else if (activeTab === 'products') { const r = await adminAPI.getProducts(); setProducts(r.data.products || []); }
        else if (activeTab === 'orders') { const r = await adminAPI.getOrders(); setOrders(r.data.orders || []); }
        else if (activeTab === 'reports') { const r = await adminAPI.getReports(); setReports(r.data.reports || []); setReportStats(r.data.stats || null); }
        else if (activeTab === 'subscriptions') { const r = await adminAPI.getSubscriptions(); setSubscriptions(r.data.subscriptions || []); setSubStats(r.data.stats || null); }
        else if (activeTab === 'approvals') { const r = await adminAPI.getPendingListings(); setPendingListings(r.data.listings || []); setPendingCount((r.data.listings || []).length); }
        else if (activeTab === 'locations') { const r = await locationsAPI.getAll(); setLocProvinces(r.data.provinces || []); setLocDistricts(r.data.districts || []); setLocSectors(r.data.sectors || []); }
        else if (activeTab === 'ads') {
          const r = await adminAPI.getAds();
          setAdsList(r.data.ads || []);
          setAdsStats(r.data.stats || null);
          setAdsPlacements(r.data.placements || []);
          setAdsSizes(r.data.sizes || {});
        }
        else if (activeTab === 'settings') {
          adminAPI.getSubscriptionPlans().then(r => setPlanConfig(r.data.plans)).catch(() => {});
          const r = await adminAPI.getSettings();
          const s = r.data.settings || [];
          const vals = {}; const meta = {};
          s.forEach(st => { vals[st.key] = st.value || ''; meta[st.key] = st; });
          setSettings(vals); setSettingsMeta(meta);
        }
      } catch (e) { console.error(e); }
      setTabLoading(false);
    })();
  }, [activeTab]);

  /* Actions */
  const toggleUserStatus = async (id, cur) => {
    await adminAPI.toggleUserStatus(id, { is_active: !cur });
    setUsers(users.map(u => u.id === id ? { ...u, is_active: !u.is_active } : u));
    showToast(cur ? 'User deactivated' : 'User activated');
  };
  const updateRole = async (id, role) => {
    await adminAPI.updateUserRole(id, { role });
    setUsers(users.map(u => u.id === id ? { ...u, role } : u));
    showToast('Role updated');
  };
  const toggleService = async (id) => {
    await adminAPI.toggleServiceStatus(id);
    setServices(services.map(s => s.id === id ? { ...s, is_active: !s.is_active } : s));
    showToast('Service updated');
  };
  const deleteService = (id) => setConfirm({
    title: 'Delete service', message: 'This action cannot be undone.', danger: true,
    onConfirm: async () => { await adminAPI.deleteService(id); setServices(services.filter(s => s.id !== id)); setConfirm(null); showToast('Service deleted'); },
  });
  const toggleProduct = async (id) => {
    await adminAPI.toggleProductStatus(id);
    setProducts(products.map(p => p.id === id ? { ...p, is_active: !p.is_active } : p));
    showToast('Product updated');
  };
  const deleteProduct = (id) => setConfirm({
    title: 'Delete product', message: 'This action cannot be undone.', danger: true,
    onConfirm: async () => { await adminAPI.deleteProduct(id); setProducts(products.filter(p => p.id !== id)); setConfirm(null); showToast('Product deleted'); },
  });


  const handleBrandingUpload = async (field, file) => {
    if (!file) return;
    setBrandingUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('field', field);
      const res = await adminAPI.uploadBranding(formData);
      setSettings(prev => ({ ...prev, [field]: res.data.url }));
      showToast(field === 'site_logo' ? 'Logo uploaded' : field === 'site_favicon' ? 'Favicon uploaded' : 'OG Image uploaded');
    } catch (err) { showToast(err.response?.data?.message || 'Upload failed', 'error'); }
    setBrandingUploading(false);
  };

  const savePlans = async () => {
    setPlansSaving(true);
    try {
      await adminAPI.updateSubscriptionPlans(planConfig);
      showToast('Plans updated successfully');
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to save plans', 'error');
    } finally {
      setPlansSaving(false);
    }
  };

  const updatePlanField = (planKey, field, value) => {
    setPlanConfig(prev => ({
      ...prev,
      [planKey]: { ...prev[planKey], [field]: value }
    }));
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      showToast('Settings saved');
    } catch (e) {
      showToast(e.response?.data?.message || 'Failed to save settings', 'error');
    }
    setSettingsSaving(false);
  };

  const filtered = (list, keys) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(i => keys.some(k => String(i[k] || '').toLowerCase().includes(q)));
  };

  /* Search bar component */
  const SearchBar = ({ placeholder }) => (
    <div className="relative">
      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
      <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={placeholder || 'Search...'}
        className="pl-8 pr-3 py-1.5 w-full sm:w-64 border border-gray-300 rounded-md text-[13px] placeholder:text-gray-400 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 bg-white transition" />
    </div>
  );

  /* Loading */
  if (loading) {
    return (
      <AdminLayout activeTab={activeTab} onTabChange={setActiveTab} pendingCount={0}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[1,2,3,4].map(i => <div key={i} className="bg-white border border-gray-200 rounded-lg p-5"><Skeleton className="h-4 w-16 mb-3" /><Skeleton className="h-7 w-24 mb-1.5" /><Skeleton className="h-3 w-20" /></div>)}
        </div>
        <Card><TableSkeleton rows={6} cols={5} /></Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab={activeTab} onTabChange={setActiveTab} pendingCount={pendingCount}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {confirm && <ConfirmModal title={confirm.title} message={confirm.message} danger={confirm.danger} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}

      {/* ═══ OVERVIEW ═══ */}
      {activeTab === 'overview' && stats && (() => {
        const s = stats.stats || {};
        const us = s.users || {};
        const sv = s.services || {};
        const pr = s.products || {};
        const bk = s.bookings || {};
        const od = s.orders || {};
        const rev = s.revenue || {};
        const growth = stats.growth || {};
        const activity = stats.recentActivity || [];
        const topSvc = stats.topServices || [];
        const topPrd = stats.topProducts || [];
        const subRev = stats.subscriptionRevenue || {};
        const dailySignups = stats.dailySignups || [];

        const rwf = (n) => Number(n || 0).toLocaleString();

        const GrowthArrow = ({ pct }) => {
          if (!pct || pct === 0) return <span className="text-[11px] text-gray-400 font-medium">—</span>;
          const up = pct > 0;
          return (
            <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-emerald-600' : 'text-red-500'}`}>
              <svg className={`w-3 h-3 ${up ? '' : 'rotate-180'}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" /></svg>
              {Math.abs(pct)}%
            </span>
          );
        };

        const maxSignup = Math.max(...(dailySignups.map(d => parseInt(d.count) || 0)), 1);
        const sparkBars = dailySignups.length > 0 ? dailySignups : Array.from({length: 7}, () => ({count: 0}));

        const activityIcons = {
          user_joined: { bg: 'bg-blue-50', fg: 'text-blue-600', icon: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z' },
          service_created: { bg: 'bg-emerald-50', fg: 'text-emerald-600', icon: 'M11.42 15.17l-5.385-2.94M11.42 15.17l5.385-2.94' },
          product_created: { bg: 'bg-purple-50', fg: 'text-purple-600', icon: 'M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z' },
          booking_made: { bg: 'bg-amber-50', fg: 'text-amber-600', icon: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75' },
        };

        const timeAgo = (d) => {
          const mins = Math.floor((Date.now() - new Date(d)) / 60000);
          if (mins < 1) return 'Just now';
          if (mins < 60) return mins + 'm ago';
          const hrs = Math.floor(mins / 60);
          if (hrs < 24) return hrs + 'h ago';
          const days = Math.floor(hrs / 24);
          return days + 'd ago';
        };

        return (
          <div className="space-y-6">
            {/* Revenue highlight */}
            <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24" />
              <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-white/60 text-[12px] font-medium uppercase tracking-wider">Total Revenue</p>
                  <p className="text-3xl sm:text-4xl font-bold mt-1 tracking-tight">{rwf(rev.booking_revenue)} <span className="text-lg font-normal text-white/50">RWF</span></p>
                  <p className="text-white/40 text-[12px] mt-1">From completed bookings</p>
                </div>
                <div className="flex gap-8">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{rwf(subRev.paid_subscribers)}</p>
                    <p className="text-white/50 text-[11px] mt-0.5">Paid Subscribers</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{rwf(subRev.active_revenue)}</p>
                    <p className="text-white/50 text-[11px] mt-0.5">Sub Revenue (RWF)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total Users', value: rwf(us.total), sub: (us.providers || 0) + ' providers \u00b7 ' + (us.customers || 0) + ' customers', growth: growth.users, color: 'blue', icon: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128H5.228A2 2 0 013.213 17.5' },
                { label: 'Services', value: rwf(sv.total), sub: (sv.active || 0) + ' active \u00b7 ' + (sv.inactive || 0) + ' hidden', growth: growth.services, color: 'emerald', icon: 'M11.42 15.17l-5.385-2.94M11.42 15.17l5.385-2.94' },
                { label: 'Products', value: rwf(pr.total), sub: (pr.active || 0) + ' active \u00b7 ' + (pr.inactive || 0) + ' hidden', growth: growth.products, color: 'purple', icon: 'M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25' },
                { label: 'Bookings', value: rwf(bk.total), sub: (bk.pending || 0) + ' pending \u00b7 ' + (bk.completed || 0) + ' done', growth: growth.bookings, color: 'amber', icon: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25' },
              ].map(card => {
                const colorMap = { blue: 'from-blue-500/10 to-blue-500/5 border-blue-200/60', emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-200/60', purple: 'from-purple-500/10 to-purple-500/5 border-purple-200/60', amber: 'from-amber-500/10 to-amber-500/5 border-amber-200/60' };
                const iconBg = { blue: 'bg-blue-100 text-blue-600', emerald: 'bg-emerald-100 text-emerald-600', purple: 'bg-purple-100 text-purple-600', amber: 'bg-amber-100 text-amber-600' };
                return (
                  <div key={card.label} className={'bg-gradient-to-br ' + colorMap[card.color] + ' border rounded-xl p-4 sm:p-5 hover:shadow-md transition-shadow'}>
                    <div className="flex items-start justify-between">
                      <div className={'w-9 h-9 rounded-lg ' + iconBg[card.color] + ' flex items-center justify-center'}>
                        <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={card.icon} /></svg>
                      </div>
                      <GrowthArrow pct={card.growth?.pct} />
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-3">{card.value}</p>
                    <p className="text-[12px] font-medium text-gray-600 mt-1">{card.label}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{card.sub}</p>
                  </div>
                );
              })}
            </div>

            {/* Secondary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Orders', value: rwf(od.total), detail: (od.pending || 0) + ' pending' },
                { label: 'Categories', value: rwf((s.categories || {}).total), detail: 'Active categories' },
                { label: 'Messages', value: rwf((s.messages || {}).total), detail: 'Total messages' },
                { label: 'All Listings', value: rwf((parseInt(sv.total)||0) + (parseInt(pr.total)||0)), detail: 'Services + Products' },
              ].map(m => (
                <div key={m.label} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
                  <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">{m.label}</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{m.value}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{m.detail}</p>
                </div>
              ))}
            </div>

            {/* Activity + Sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Recent Activity */}
              <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-[14px] font-semibold text-gray-900">Recent Activity</h3>
                  <span className="text-[11px] text-gray-400">Last 10 events</span>
                </div>
                {activity.length === 0 ? (
                  <div className="p-8 text-center text-[13px] text-gray-400">No recent activity</div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {activity.map((a, i) => {
                      const cfg = activityIcons[a.type] || activityIcons.user_joined;
                      return (
                        <div key={i} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50/50 transition">
                          <div className={'w-8 h-8 rounded-lg ' + cfg.bg + ' flex items-center justify-center flex-shrink-0'}>
                            <svg className={'w-4 h-4 ' + cfg.fg} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={cfg.icon} /></svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-gray-800 truncate">{a.title}</p>
                            <p className="text-[11px] text-gray-400 capitalize">{a.type.replace(/_/g, ' ')}</p>
                          </div>
                          <span className="text-[11px] text-gray-400 flex-shrink-0">{timeAgo(a.created_at)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-5">
                {/* Signups chart */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-[13px] font-semibold text-gray-900 mb-3">User Signups (7 days)</h3>
                  <div className="flex items-end gap-1.5 h-16">
                    {sparkBars.map((d, i) => {
                      const pct = maxSignup > 0 ? Math.max(((parseInt(d.count) || 0) / maxSignup) * 100, 6) : 6;
                      const isToday = i === sparkBars.length - 1;
                      return (
                        <div key={i} className="flex-1" title={(d.count || 0) + ' signups'}>
                          <div className={'w-full rounded-sm transition-all ' + (isToday ? 'bg-blue-500' : 'bg-blue-200')} style={{ height: pct + '%' }} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-2 text-[10px] text-gray-400">
                    <span>7d ago</span><span>Today</span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-[13px] font-semibold text-gray-900 mb-3">Quick Actions</h3>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Manage Users', tab: 'users', color: 'text-blue-600 bg-blue-50', icon: 'M15 19.128a9.38 9.38 0 002.625.372' },
                      { label: 'Review Approvals', tab: 'approvals', color: 'text-amber-600 bg-amber-50', icon: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z', badge: pendingCount },
                      { label: 'Subscriptions', tab: 'subscriptions', color: 'text-purple-600 bg-purple-50', icon: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z' },
                      { label: 'Settings', tab: 'settings', color: 'text-gray-600 bg-gray-100', icon: 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281' },
                    ].map(qa => (
                      <button key={qa.tab} onClick={() => setActiveTab(qa.tab)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition text-left group">
                        <div className={'w-8 h-8 rounded-lg ' + qa.color + ' flex items-center justify-center flex-shrink-0'}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={qa.icon} /></svg>
                        </div>
                        <span className="text-[13px] font-medium text-gray-700 flex-1">{qa.label}</span>
                        {qa.badge > 0 && <span className="text-[11px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">{qa.badge}</span>}
                        <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Top Performers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Top Services */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-[14px] font-semibold text-gray-900">Top Services</h3>
                  <button onClick={() => setActiveTab('services')} className="text-[12px] text-blue-500 hover:text-blue-700 font-medium">View All &rarr;</button>
                </div>
                {topSvc.length === 0 ? (
                  <div className="p-6 text-center text-[13px] text-gray-400">No services yet</div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {topSvc.map((svc, i) => (
                      <div key={svc.id} className="flex items-center gap-3 px-5 py-3">
                        <span className={'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ' + (i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-200 text-gray-600' : 'bg-gray-100 text-gray-500')}>
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-gray-800 truncate">{svc.title}</p>
                          <p className="text-[11px] text-gray-400">{svc.provider_name}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[12px] font-semibold text-gray-900">{rwf(svc.views)} views</p>
                          <p className="text-[11px] text-gray-400">{rwf(svc.price)} RWF</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Top Products */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-[14px] font-semibold text-gray-900">Top Products</h3>
                  <button onClick={() => setActiveTab('products')} className="text-[12px] text-blue-500 hover:text-blue-700 font-medium">View All &rarr;</button>
                </div>
                {topPrd.length === 0 ? (
                  <div className="p-6 text-center text-[13px] text-gray-400">No products yet</div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {topPrd.map((prd, i) => (
                      <div key={prd.id} className="flex items-center gap-3 px-5 py-3">
                        <span className={'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ' + (i === 0 ? 'bg-purple-100 text-purple-700' : i === 1 ? 'bg-gray-200 text-gray-600' : 'bg-gray-100 text-gray-500')}>
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-gray-800 truncate">{prd.product_name}</p>
                          <p className="text-[11px] text-gray-400">{prd.seller_name}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[12px] font-semibold text-gray-900">{rwf(prd.views)} views</p>
                          <p className="text-[11px] text-gray-400">{rwf(prd.price)} RWF</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Pending alert */}
            {pendingCount > 0 && (
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" /></svg>
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-amber-900">{pendingCount} listing{pendingCount > 1 ? 's' : ''} awaiting review</p>
                    <p className="text-[12px] text-amber-700/70">These need your approval before going live</p>
                  </div>
                </div>
                <button onClick={() => setActiveTab('approvals')} className="px-4 py-2 text-[13px] font-semibold text-amber-800 bg-amber-200 hover:bg-amber-300 rounded-lg transition shadow-sm">
                  Review Now
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ USERS ═══ */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-gray-500">{users.length} total users</p>
            <SearchBar placeholder="Search users..." />
          </div>
          <Card>
            {tabLoading ? <TableSkeleton rows={8} cols={5} /> : users.length === 0 ? (
              <EmptyState title="No users yet" subtitle="Users will appear once they register" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><TH>Name</TH><TH>Email</TH><TH>Role</TH><TH>Status</TH><TH>Joined</TH><TH>Actions</TH></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered(users, ['first_name', 'last_name', 'email']).map(u => (
                      <tr key={u.id} className="hover:bg-gray-50/60 transition-colors">
                        <TD className="font-medium text-gray-900">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] font-semibold text-gray-500 flex-shrink-0">{u.first_name?.[0]}{u.last_name?.[0]}</div>
                            {u.first_name} {u.last_name}
                          </div>
                        </TD>
                        <TD className="text-gray-500">{u.email}</TD>
                        <TD>
                          <select value={u.role} onChange={e => updateRole(u.id, e.target.value)}
                            className="text-[12px] border border-gray-200 rounded-md px-2 py-1 bg-white cursor-pointer focus:ring-1 focus:ring-gray-400 text-gray-700">
                            <option value="customer">Customer</option>
                            <option value="provider">Provider</option>
                            <option value="admin">Admin</option>
                          </select>
                        </TD>
                        <TD><StatusBadge status={u.is_active ? 'active' : 'inactive'} /></TD>
                        <TD className="text-gray-400 text-[12px]">{new Date(u.created_at).toLocaleDateString()}</TD>
                        <TD>
                          <button onClick={() => toggleUserStatus(u.id, u.is_active)}
                            className={`text-[12px] font-medium px-2.5 py-1 rounded-md border transition ${u.is_active ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`}>
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ SERVICES ═══ */}
      {activeTab === 'services' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-gray-500">{services.length} services</p>
            <SearchBar placeholder="Search services..." />
          </div>
          <Card>
            {tabLoading ? <TableSkeleton rows={8} cols={5} /> : services.length === 0 ? (
              <EmptyState title="No services" subtitle="Services will appear here" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><TH>Service</TH><TH>Provider</TH><TH>Category</TH><TH>Price</TH><TH>Status</TH><TH>Actions</TH></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered(services, ['title', 'category_name']).map(s => (
                      <tr key={s.id} className="hover:bg-gray-50/60 transition-colors">
                        <TD className="font-medium text-gray-900 max-w-xs truncate">{s.title}</TD>
                        <TD className="text-gray-500">{s.provider_name || '—'}</TD>
                        <TD><span className="text-[12px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{s.category_name || '—'}</span></TD>
                        <TD className="font-medium text-gray-900 tabular-nums">{Number(s.price).toLocaleString()} RWF</TD>
                        <TD><StatusBadge status={s.is_active ? 'active' : 'inactive'} /></TD>
                        <TD>
                          <div className="flex gap-1.5">
                            <button onClick={() => toggleService(s.id)} className={`text-[12px] font-medium px-2.5 py-1 rounded-md border transition ${s.is_active ? 'text-gray-600 border-gray-200 hover:bg-gray-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`}>
                              {s.is_active ? 'Disable' : 'Enable'}
                            </button>
                            <button onClick={() => deleteService(s.id)} className="text-[12px] font-medium px-2.5 py-1 rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition">Delete</button>
                          </div>
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ PRODUCTS ═══ */}
      {activeTab === 'products' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-gray-500">{products.length} products</p>
            <SearchBar placeholder="Search products..." />
          </div>
          <Card>
            {tabLoading ? <TableSkeleton rows={8} cols={6} /> : products.length === 0 ? (
              <EmptyState title="No products" subtitle="Products will appear here" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><TH>Product</TH><TH>Seller</TH><TH>Category</TH><TH>Price</TH><TH>Stock</TH><TH>Status</TH><TH>Actions</TH></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered(products, ['product_name', 'category_name']).map(p => (
                      <tr key={p.id} className="hover:bg-gray-50/60 transition-colors">
                        <TD className="font-medium text-gray-900 max-w-xs truncate">{p.product_name}</TD>
                        <TD className="text-gray-500">{p.seller_name || '—'}</TD>
                        <TD><span className="text-[12px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{p.category_name || '—'}</span></TD>
                        <TD className="font-medium text-gray-900 tabular-nums">{Number(p.price).toLocaleString()} RWF</TD>
                        <TD className="text-gray-600 tabular-nums">{p.stock}</TD>
                        <TD><StatusBadge status={p.is_active !== false ? 'active' : 'inactive'} /></TD>
                        <TD>
                          <div className="flex gap-1.5">
                            <button onClick={() => toggleProduct(p.id)} className={`text-[12px] font-medium px-2.5 py-1 rounded-md border transition ${p.is_active !== false ? 'text-gray-600 border-gray-200 hover:bg-gray-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`}>
                              {p.is_active !== false ? 'Disable' : 'Enable'}
                            </button>
                            <button onClick={() => deleteProduct(p.id)} className="text-[12px] font-medium px-2.5 py-1 rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition">Delete</button>
                          </div>
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ BOOKINGS ═══ */}
      {activeTab === 'bookings' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-gray-500">{bookings.length} bookings</p>
            <SearchBar placeholder="Search bookings..." />
          </div>
          <Card>
            {tabLoading ? <TableSkeleton rows={8} cols={5} /> : bookings.length === 0 ? (
              <EmptyState title="No bookings" subtitle="Bookings will appear here" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><TH>Service</TH><TH>Customer</TH><TH>Provider</TH><TH>Date</TH><TH>Amount</TH><TH>Status</TH></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered(bookings, ['service_title', 'customer_name']).map(b => (
                      <tr key={b.id} className="hover:bg-gray-50/60 transition-colors">
                        <TD className="font-medium text-gray-900">{b.service_title}</TD>
                        <TD className="text-gray-500">{b.customer_name || '—'}</TD>
                        <TD className="text-gray-500">{b.provider_name || '—'}</TD>
                        <TD className="text-gray-400 text-[12px] tabular-nums">{new Date(b.booking_date).toLocaleDateString()}</TD>
                        <TD className="font-medium text-gray-900 tabular-nums">{Number(b.total_price).toLocaleString()} RWF</TD>
                        <TD><StatusBadge status={b.status} /></TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ ORDERS ═══ */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-gray-500">{orders.length} orders</p>
            <SearchBar placeholder="Search orders..." />
          </div>
          <Card>
            {tabLoading ? <TableSkeleton rows={8} cols={5} /> : orders.length === 0 ? (
              <EmptyState title="No orders" subtitle="Orders will appear here" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><TH>Product</TH><TH>Customer</TH><TH>Qty</TH><TH>Total</TH><TH>Status</TH><TH>Date</TH></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered(orders, ['product_name', 'customer_name']).map(o => (
                      <tr key={o.id} className="hover:bg-gray-50/60 transition-colors">
                        <TD className="font-medium text-gray-900">{o.product_name || '—'}</TD>
                        <TD className="text-gray-500">{o.customer_name || '—'}</TD>
                        <TD className="text-gray-600 tabular-nums">{o.quantity}</TD>
                        <TD className="font-medium text-gray-900 tabular-nums">{Number(o.total_price).toLocaleString()} RWF</TD>
                        <TD><StatusBadge status={o.status} /></TD>
                        <TD className="text-gray-400 text-[12px] tabular-nums">{new Date(o.created_at).toLocaleDateString()}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ PAYMENTS ═══ */}
      {activeTab === 'payments' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-gray-500">{payments.length} transactions</p>
            <SearchBar placeholder="Search transactions..." />
          </div>
          <Card>
            {tabLoading ? <TableSkeleton rows={8} cols={5} /> : payments.length === 0 ? (
              <EmptyState title="No transactions" subtitle="Transactions will appear here" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><TH>ID</TH><TH>User</TH><TH>Method</TH><TH>Amount</TH><TH>Status</TH><TH>Date</TH></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered(payments, ['transaction_id', 'user_name']).map(p => (
                      <tr key={p.id} className="hover:bg-gray-50/60 transition-colors">
                        <TD className="font-mono text-[12px] text-gray-500">{p.transaction_id || p.id}</TD>
                        <TD className="text-gray-500">{p.user_name || '—'}</TD>
                        <TD className="text-gray-500 capitalize">{p.payment_method?.replace('_', ' ')}</TD>
                        <TD className="font-medium text-gray-900 tabular-nums">{Number(p.amount).toLocaleString()} RWF</TD>
                        <TD><StatusBadge status={p.status} /></TD>
                        <TD className="text-gray-400 text-[12px] tabular-nums">{new Date(p.created_at).toLocaleDateString()}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ REPORTS ═══ */}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          {reportStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 rounded-lg overflow-hidden border border-gray-200">
              {[
                { label: 'Pending', value: reportStats.byStatus?.pending || 0, dot: 'bg-amber-400' },
                { label: 'Reviewed', value: reportStats.byStatus?.reviewed || 0, dot: 'bg-blue-500' },
                { label: 'Resolved', value: reportStats.byStatus?.resolved || 0, dot: 'bg-emerald-500' },
                { label: 'Dismissed', value: reportStats.byStatus?.dismissed || 0, dot: 'bg-gray-300' },
              ].map(s => (
                <div key={s.label} className="bg-white p-4 flex items-center gap-3">
                  <Dot color={s.dot} />
                  <div>
                    <p className="text-[18px] font-semibold text-gray-900 leading-none">{s.value}</p>
                    <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider mt-1">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-end">
            <SearchBar placeholder="Search reports..." />
          </div>
          <Card>
            {tabLoading ? <TableSkeleton rows={6} cols={6} /> : reports.length === 0 ? (
              <EmptyState title="No reports" subtitle="Reports will appear here" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><TH>Reporter</TH><TH>Type</TH><TH>Reason</TH><TH>Status</TH><TH>Date</TH><TH>Actions</TH></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered(reports, ['reporter_name', 'reason', 'reported_type']).map(r => (
                      <tr key={r.id} className="hover:bg-gray-50/60 transition-colors">
                        <TD className="text-gray-700">{r.reporter_name || `User #${r.reporter_id}`}</TD>
                        <TD><span className="text-[12px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{r.reported_type}</span></TD>
                        <TD className="text-gray-600 capitalize">{r.reason}</TD>
                        <TD><StatusBadge status={r.status} /></TD>
                        <TD className="text-gray-400 text-[12px] tabular-nums">{new Date(r.created_at).toLocaleDateString()}</TD>
                        <TD>
                          {r.status === 'pending' && (
                            <div className="flex gap-1.5">
                              <button onClick={async () => { await adminAPI.updateReport(r.id, { status: 'resolved', admin_notes: 'Resolved by admin' }); setReports(reports.map(x => x.id === r.id ? { ...x, status: 'resolved' } : x)); showToast('Resolved'); }}
                                className="text-[12px] font-medium px-2.5 py-1 rounded-md border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition">Resolve</button>
                              <button onClick={async () => { await adminAPI.updateReport(r.id, { status: 'dismissed', admin_notes: 'Dismissed by admin' }); setReports(reports.map(x => x.id === r.id ? { ...x, status: 'dismissed' } : x)); showToast('Dismissed'); }}
                                className="text-[12px] font-medium px-2.5 py-1 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 transition">Dismiss</button>
                            </div>
                          )}
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ SUBSCRIPTIONS ═══ */}
      {activeTab === 'subscriptions' && (
        <div className="space-y-4">
          {subStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 rounded-lg overflow-hidden border border-gray-200">
              {[
                { label: 'Free', value: subStats.byPlan?.free || 0 },
                { label: 'Basic', value: subStats.byPlan?.basic || 0 },
                { label: 'Premium', value: subStats.byPlan?.premium || 0 },
                { label: 'Revenue', value: `${Number(subStats.revenue || 0).toLocaleString()} RWF` },
              ].map(s => (
                <div key={s.label} className="bg-white p-4">
                  <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">{s.label}</p>
                  <p className="text-[18px] font-semibold text-gray-900 mt-1 leading-none">{s.value}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-end">
            <SearchBar placeholder="Search subscriptions..." />
          </div>
          <Card>
            {tabLoading ? <TableSkeleton rows={6} cols={5} /> : subscriptions.length === 0 ? (
              <EmptyState title="No subscriptions" subtitle="Subscriptions will appear here" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><TH>User</TH><TH>Plan</TH><TH>Status</TH><TH>Max Listings</TH><TH>Price</TH><TH>Expires</TH></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered(subscriptions, ['first_name', 'last_name', 'plan']).map(s => (
                      <tr key={s.id} className="hover:bg-gray-50/60 transition-colors">
                        <TD className="font-medium text-gray-900">{s.first_name} {s.last_name}</TD>
                        <TD><span className={`text-[12px] font-medium px-2 py-0.5 rounded ${s.plan === 'premium' ? 'bg-violet-100 text-violet-700' : s.plan === 'basic' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{s.plan}</span></TD>
                        <TD><StatusBadge status={s.status} /></TD>
                        <TD className="text-gray-600 tabular-nums">{s.max_listings}</TD>
                        <TD className="font-medium text-gray-900 tabular-nums">{Number(s.price || 0).toLocaleString()} RWF</TD>
                        <TD className="text-gray-400 text-[12px] tabular-nums">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : '—'}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ APPROVALS ═══ */}
      {activeTab === 'approvals' && (
        <div className="space-y-4">
          <p className="text-[13px] text-gray-500">{pendingListings.length} pending</p>
          <Card>
            {tabLoading ? <TableSkeleton rows={6} cols={6} /> : pendingListings.length === 0 ? (
              <EmptyState title="All clear" subtitle="No pending listings to review" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><TH>Listing</TH><TH>Type</TH><TH>Owner</TH><TH>Category</TH><TH>Price</TH><TH>Date</TH><TH>Actions</TH></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {pendingListings.map(l => (
                      <tr key={`${l.type}-${l.id}`} className="hover:bg-gray-50/60 transition-colors">
                        <TD className="font-medium text-gray-900 max-w-xs truncate">{l.title || l.product_name}</TD>
                        <TD><span className={`text-[12px] font-medium px-2 py-0.5 rounded ${l.type === 'service' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}`}>{l.type}</span></TD>
                        <TD className="text-gray-500">{l.first_name} {l.last_name}</TD>
                        <TD><span className="text-[12px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{l.category_name}</span></TD>
                        <TD className="font-medium text-gray-900 tabular-nums">{Number(l.price).toLocaleString()} RWF</TD>
                        <TD className="text-gray-400 text-[12px] tabular-nums">{new Date(l.created_at).toLocaleDateString()}</TD>
                        <TD>
                          <div className="flex gap-1.5">
                            <button onClick={async () => {
                              if (l.type === 'service') await adminAPI.approveService(l.id); else await adminAPI.approveProduct(l.id);
                              setPendingListings(prev => prev.filter(x => !(x.type === l.type && x.id === l.id)));
                              setPendingCount(c => c - 1); showToast('Approved');
                            }} className="text-[12px] font-medium px-2.5 py-1 rounded-md border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition">Approve</button>
                            <button onClick={async () => {
                              if (l.type === 'service') await adminAPI.rejectService(l.id); else await adminAPI.rejectProduct(l.id);
                              setPendingListings(prev => prev.filter(x => !(x.type === l.type && x.id === l.id)));
                              setPendingCount(c => c - 1); showToast('Rejected');
                            }} className="text-[12px] font-medium px-2.5 py-1 rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition">Reject</button>
                          </div>
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ LOCATIONS ═══ */}
      {activeTab === 'locations' && (
        <div className="space-y-4">
          {/* Form */}
          <Card className="p-5">
            <p className="text-[13px] font-medium text-gray-900 mb-3">{locForm.editing ? 'Edit location' : 'Add location'}</p>
            <div className="flex flex-wrap gap-2.5 items-end">
              <div>
                <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1 block">Type</label>
                <select value={locForm.type} onChange={e => setLocForm({...locForm, type: e.target.value, parent_id: '', editing: null})}
                  className="border border-gray-300 rounded-md px-2.5 py-1.5 text-[13px] bg-white focus:ring-1 focus:ring-gray-400">
                  <option value="">Select</option>
                  <option value="province">Province</option>
                  <option value="district">District</option>
                  <option value="sector">Sector</option>
                </select>
              </div>
              {locForm.type === 'district' && (
                <div>
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1 block">Province</label>
                  <select value={locForm.parent_id} onChange={e => setLocForm({...locForm, parent_id: e.target.value})}
                    className="border border-gray-300 rounded-md px-2.5 py-1.5 text-[13px] bg-white focus:ring-1 focus:ring-gray-400">
                    <option value="">Select</option>
                    {locProvinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              {locForm.type === 'sector' && (
                <div>
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1 block">District</label>
                  <select value={locForm.parent_id} onChange={e => setLocForm({...locForm, parent_id: e.target.value})}
                    className="border border-gray-300 rounded-md px-2.5 py-1.5 text-[13px] bg-white focus:ring-1 focus:ring-gray-400">
                    <option value="">Select</option>
                    {locDistricts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1 block">Name</label>
                <input type="text" value={locForm.name} onChange={e => setLocForm({...locForm, name: e.target.value})}
                  placeholder="Name" className="border border-gray-300 rounded-md px-2.5 py-1.5 text-[13px] focus:ring-1 focus:ring-gray-400" />
              </div>
              <button onClick={async () => {
                if (!locForm.type || !locForm.name.trim()) return;
                try {
                  if (locForm.editing) {
                    if (locForm.type === 'province') await locationsAPI.updateProvince(locForm.editing, { name: locForm.name });
                    else if (locForm.type === 'district') await locationsAPI.updateDistrict(locForm.editing, { name: locForm.name, province_id: locForm.parent_id });
                    else await locationsAPI.updateSector(locForm.editing, { name: locForm.name, district_id: locForm.parent_id });
                  } else {
                    if (locForm.type === 'province') await locationsAPI.createProvince({ name: locForm.name });
                    else if (locForm.type === 'district') await locationsAPI.createDistrict({ name: locForm.name, province_id: locForm.parent_id });
                    else await locationsAPI.createSector({ name: locForm.name, district_id: locForm.parent_id });
                  }
                  const r = await locationsAPI.getAll();
                  setLocProvinces(r.data.provinces || []); setLocDistricts(r.data.districts || []); setLocSectors(r.data.sectors || []);
                  setLocForm({ type: '', parent_id: '', name: '', editing: null });
                  showToast(locForm.editing ? 'Updated' : 'Added');
                } catch (err) { showToast(err.response?.data?.error || 'Failed', 'error'); }
              }} className="text-[13px] font-medium text-white bg-gray-900 px-3.5 py-1.5 rounded-md hover:bg-gray-800 transition shadow-sm">
                {locForm.editing ? 'Update' : 'Add'}
              </button>
              {locForm.editing && (
                <button onClick={() => setLocForm({ type: '', parent_id: '', name: '', editing: null })}
                  className="text-[13px] font-medium text-gray-600 bg-white border border-gray-300 px-3.5 py-1.5 rounded-md hover:bg-gray-50 transition">Cancel</button>
              )}
            </div>
          </Card>

          {/* Tree */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[13px] font-medium text-gray-900">All locations</p>
              <div className="flex gap-3 text-[11px] font-medium text-gray-400">
                <span>{locProvinces.length} provinces</span>
                <span>·</span>
                <span>{locDistricts.length} districts</span>
                <span>·</span>
                <span>{locSectors.length} sectors</span>
              </div>
            </div>
            <div className="space-y-px">
              {locProvinces.map(prov => {
                const pd = locDistricts.filter(d => d.province_id === prov.id);
                return (
                  <div key={prov.id} className="border border-gray-200 rounded-md overflow-hidden mb-1.5">
                    <div className="flex items-center justify-between px-3.5 py-2.5 hover:bg-gray-50 cursor-pointer transition"
                      onClick={() => setLocExpanded(p => ({...p, [`p${prov.id}`]: !p[`p${prov.id}`]}))}>
                      <div className="flex items-center gap-2">
                        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${locExpanded[`p${prov.id}`] ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                        <span className="text-[13px] font-medium text-gray-900">{prov.name}</span>
                        <span className="text-[11px] text-gray-400">{pd.length}</span>
                      </div>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setLocForm({ type: 'province', parent_id: '', name: prov.name, editing: prov.id })} className="text-[11px] text-gray-500 hover:text-gray-700 px-1.5 py-0.5 rounded hover:bg-gray-100 transition">Edit</button>
                        <button onClick={() => setConfirm({ title: 'Delete province', message: `Delete "${prov.name}" and all children?`, danger: true,
                          onConfirm: async () => { await locationsAPI.deleteProvince(prov.id); const r = await locationsAPI.getAll(); setLocProvinces(r.data.provinces || []); setLocDistricts(r.data.districts || []); setLocSectors(r.data.sectors || []); setConfirm(null); showToast('Deleted'); },
                        })} className="text-[11px] text-red-500 hover:text-red-700 px-1.5 py-0.5 rounded hover:bg-red-50 transition">Delete</button>
                      </div>
                    </div>
                    {locExpanded[`p${prov.id}`] && (
                      <div className="border-t border-gray-100 bg-gray-50/40 pl-7">
                        {pd.length === 0 && <div className="py-2 text-[12px] text-gray-400 px-3.5">No districts</div>}
                        {pd.map(dist => {
                          const ds = locSectors.filter(s => s.district_id === dist.id);
                          return (
                            <div key={dist.id} className="border-b border-gray-100 last:border-0">
                              <div className="flex items-center justify-between px-3.5 py-2 hover:bg-gray-100/50 cursor-pointer transition"
                                onClick={() => setLocExpanded(p => ({...p, [`d${dist.id}`]: !p[`d${dist.id}`]}))}>
                                <div className="flex items-center gap-2">
                                  <svg className={`w-3 h-3 text-gray-400 transition-transform ${locExpanded[`d${dist.id}`] ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                                  <span className="text-[13px] text-gray-700">{dist.name}</span>
                                  <span className="text-[11px] text-gray-400">{ds.length}</span>
                                </div>
                                <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                  <button onClick={() => setLocForm({ type: 'district', parent_id: String(prov.id), name: dist.name, editing: dist.id })} className="text-[11px] text-gray-500 hover:text-gray-700 px-1.5 py-0.5 rounded hover:bg-gray-100 transition">Edit</button>
                                  <button onClick={() => setConfirm({ title: 'Delete district', message: `Delete "${dist.name}"?`, danger: true,
                                    onConfirm: async () => { await locationsAPI.deleteDistrict(dist.id); const r = await locationsAPI.getAll(); setLocProvinces(r.data.provinces || []); setLocDistricts(r.data.districts || []); setLocSectors(r.data.sectors || []); setConfirm(null); showToast('Deleted'); },
                                  })} className="text-[11px] text-red-500 hover:text-red-700 px-1.5 py-0.5 rounded hover:bg-red-50 transition">Delete</button>
                                </div>
                              </div>
                              {locExpanded[`d${dist.id}`] && (
                                <div className="border-t border-gray-100/60 bg-white/60 pl-7">
                                  {ds.length === 0 && <div className="py-2 text-[12px] text-gray-400 px-3.5">No sectors</div>}
                                  {ds.map(sec => (
                                    <div key={sec.id} className="flex items-center justify-between px-3.5 py-1.5 hover:bg-gray-50 transition">
                                      <span className="text-[13px] text-gray-600">{sec.name}</span>
                                      <div className="flex gap-1">
                                        <button onClick={() => setLocForm({ type: 'sector', parent_id: String(dist.id), name: sec.name, editing: sec.id })} className="text-[11px] text-gray-500 hover:text-gray-700 px-1.5 py-0.5 rounded hover:bg-gray-100 transition">Edit</button>
                                        <button onClick={() => setConfirm({ title: 'Delete sector', message: `Delete "${sec.name}"?`, danger: true,
                                          onConfirm: async () => { await locationsAPI.deleteSector(sec.id); const r = await locationsAPI.getAll(); setLocProvinces(r.data.provinces || []); setLocDistricts(r.data.districts || []); setLocSectors(r.data.sectors || []); setConfirm(null); showToast('Deleted'); },
                                        })} className="text-[11px] text-red-500 hover:text-red-700 px-1.5 py-0.5 rounded hover:bg-red-50 transition">Delete</button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* ═══ AD BANNERS ═══ */}
      {activeTab === 'ads' && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[18px] font-semibold text-gray-900">Ad Banners</h2>
              <p className="text-[13px] text-gray-500 mt-0.5">Manage banner advertisements across the site</p>
            </div>
            <button onClick={() => setAdsForm({ title: '', image_url: '', link_url: '', placement: 'homepage-top', size: 'leaderboard', is_active: true, starts_at: '', expires_at: '', sort_order: 0 })}
              className="px-4 py-2 bg-gray-900 text-white text-[13px] font-medium rounded-lg hover:bg-gray-800 transition flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              New Banner
            </button>
          </div>

          {/* Stats */}
          {adsStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Total Banners', value: adsStats.total || 0, icon: '🖼️' },
                { label: 'Active', value: adsStats.active || 0, icon: '✅' },
                { label: 'Total Views', value: Number(adsStats.total_views || 0).toLocaleString(), icon: '👁️' },
                { label: 'Total Clicks', value: Number(adsStats.total_clicks || 0).toLocaleString(), icon: '🖱️' },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span>{s.icon}</span>
                    <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">{s.label}</p>
                  </div>
                  <p className="text-[20px] font-bold text-gray-900">{s.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Banner Form Modal */}
          {adsForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setAdsForm(null)}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-gray-100">
                  <h3 className="text-[16px] font-semibold text-gray-900">{adsForm.id ? 'Edit Banner' : 'Create New Banner'}</h3>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-[12px] font-medium text-gray-600 mb-1">Title *</label>
                    <input type="text" value={adsForm.title || ''} onChange={e => setAdsForm({...adsForm, title: e.target.value})}
                      placeholder="e.g. Summer Sale Banner"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[13px] focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-gray-600 mb-1">Banner Image *</label>
                    {adsForm.image_url && !adsForm._newFile && (
                      <div className="mb-2 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                        <img src={`${(process.env.REACT_APP_API_URL || '').replace('/api', '')}${adsForm.image_url}`} alt="" className="max-h-32 mx-auto object-contain" />
                      </div>
                    )}
                    {adsForm._preview && (
                      <div className="mb-2 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                        <img src={adsForm._preview} alt="Preview" className="max-h-32 mx-auto object-contain" />
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={e => {
                      const file = e.target.files[0];
                      if (file) {
                        setAdsForm({...adsForm, _newFile: file, _preview: URL.createObjectURL(file)});
                      }
                    }} className="w-full text-[13px] text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-100 file:text-gray-700 file:font-medium file:cursor-pointer hover:file:bg-gray-200" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-gray-600 mb-1">Link URL (optional)</label>
                    <input type="url" value={adsForm.link_url || ''} onChange={e => setAdsForm({...adsForm, link_url: e.target.value})}
                      placeholder="https://example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[13px] focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-medium text-gray-600 mb-1">Placement *</label>
                      <select value={adsForm.placement || 'homepage-top'} onChange={e => setAdsForm({...adsForm, placement: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[13px] focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 bg-white">
                        {adsPlacements.map(p => (
                          <option key={p.key} value={p.key}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-gray-600 mb-1">Banner Size</label>
                      <select value={adsForm.size || 'leaderboard'} onChange={e => setAdsForm({...adsForm, size: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[13px] focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 bg-white">
                        {Object.entries(adsSizes).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-medium text-gray-600 mb-1">Start Date (optional)</label>
                      <input type="date" value={adsForm.starts_at ? adsForm.starts_at.split('T')[0] : ''} onChange={e => setAdsForm({...adsForm, starts_at: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[13px] focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400" />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-gray-600 mb-1">Expiry Date (optional)</label>
                      <input type="date" value={adsForm.expires_at ? adsForm.expires_at.split('T')[0] : ''} onChange={e => setAdsForm({...adsForm, expires_at: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[13px] focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-medium text-gray-600 mb-1">Sort Order</label>
                      <input type="number" min="0" value={adsForm.sort_order || 0} onChange={e => setAdsForm({...adsForm, sort_order: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[13px] focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400" />
                      <p className="text-[10px] text-gray-400 mt-1">Lower = shown first</p>
                    </div>
                    <div className="flex items,center pt-5">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <button type="button" onClick={() => setAdsForm({...adsForm, is_active: !adsForm.is_active})}
                          className={`relative w-10 h-[22px] rounded-full transition-colors ${adsForm.is_active ? 'bg-green-500' : 'bg-gray-200'}`}>
                          <div className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white shadow transition-transform ${adsForm.is_active ? 'translate-x-[20px]' : 'translate-x-0.5'}`} />
                        </button>
                        <span className="text-[13px] text-gray-700">{adsForm.is_active ? 'Active' : 'Paused'}</span>
                      </label>
                    </div>
                  </div>
                  {/* Placement info */}
                  {adsPlacements.find(p => p.key === adsForm.placement) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-[11px] font-medium text-blue-700 mb-0.5">📐 {adsPlacements.find(p => p.key === adsForm.placement)?.label}</p>
                      <p className="text-[11px] text-blue-600">Recommended size: <strong>{adsPlacements.find(p => p.key === adsForm.placement)?.size}</strong></p>
                      <p className="text-[10px] text-blue-500 mt-0.5">{adsPlacements.find(p => p.key === adsForm.placement)?.desc}</p>
                    </div>
                  )}
                </div>
                <div className="p-5 border-t border-gray-100 flex gap-3">
                  <button onClick={() => setAdsForm(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-medium text-gray-700 hover:bg-gray-50 transition">Cancel</button>
                  <button disabled={adsUploading} onClick={async () => {
                    if (!adsForm.title) return showToast('Title is required', 'error');
                    if (!adsForm.image_url && !adsForm._newFile) return showToast('Image is required', 'error');
                    setAdsUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append('title', adsForm.title);
                      fd.append('placement', adsForm.placement || 'homepage-top');
                      fd.append('size', adsForm.size || 'leaderboard');
                      fd.append('is_active', adsForm.is_active);
                      fd.append('sort_order', adsForm.sort_order || 0);
                      if (adsForm.link_url) fd.append('link_url', adsForm.link_url);
                      if (adsForm.starts_at) fd.append('starts_at', adsForm.starts_at);
                      if (adsForm.expires_at) fd.append('expires_at', adsForm.expires_at);
                      if (adsForm._newFile) fd.append('image', adsForm._newFile);
                      else if (adsForm.image_url) fd.append('image_url', adsForm.image_url);

                      if (adsForm.id) {
                        await adminAPI.updateAd(adsForm.id, fd);
                        showToast('Banner updated');
                      } else {
                        await adminAPI.createAd(fd);
                        showToast('Banner created');
                      }
                      setAdsForm(null);
                      const r = await adminAPI.getAds();
                      setAdsList(r.data.ads || []);
                      setAdsStats(r.data.stats || null);
                    } catch (err) {
                      showToast(err.response?.data?.error || 'Failed to save banner', 'error');
                    } finally {
                      setAdsUploading(false);
                    }
                  }} className="flex-1 py-2.5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 transition disabled:opacity-50">
                    {adsUploading ? 'Saving...' : adsForm.id ? 'Update Banner' : 'Create Banner'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Banners List */}
          {tabLoading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="bg-white rounded-xl border border-gray-100 h-24 animate-pulse" />)}</div>
          ) : adsList.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="text-5xl mb-3">🖼️</div>
              <p className="font-semibold text-gray-900">No banners yet</p>
              <p className="text-[13px] text-gray-400 mt-1 mb-4">Create your first ad banner to promote on the site</p>
              <button onClick={() => setAdsForm({ title: '', image_url: '', link_url: '', placement: 'homepage-top', size: 'leaderboard', is_active: true, starts_at: '', expires_at: '', sort_order: 0 })}
                className="px-5 py-2 bg-gray-900 text-white text-[13px] font-medium rounded-lg hover:bg-gray-800 transition">
                Create First Banner
              </button>
            </Card>
          ) : (
            <div className="space-y-3">
              {adsList.map(ad => {
                const ctr = ad.view_count > 0 ? ((ad.click_count / ad.view_count) * 100).toFixed(2) : '0.00';
                const isExpired = ad.expires_at && new Date(ad.expires_at) < new Date();
                const placementInfo = adsPlacements.find(p => p.key === ad.placement);
                return (
                  <Card key={ad.id} className="p-0 overflow-hidden">
                    <div className="flex flex-col sm:flex-row">
                      {/* Image Preview */}
                      <div className="sm:w-48 h-32 sm:h-auto bg-gray-100 flex-shrink-0 relative overflow-hidden">
                        <img src={`${(process.env.REACT_APP_API_URL || '').replace('/api', '')}${ad.image_url}`} alt={ad.title}
                          className="w-full h-full object-cover" />
                        {!ad.is_active && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <span className="text-white text-[11px] font-bold bg-black/60 px-2 py-1 rounded">PAUSED</span>
                          </div>
                        )}
                        {isExpired && (
                          <div className="absolute inset-0 bg-red-900/30 flex items-center justify-center">
                            <span className="text-white text-[11px] font-bold bg-red-600 px-2 py-1 rounded">EXPIRED</span>
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h4 className="font-semibold text-gray-900 text-[14px]">{ad.title}</h4>
                            <div className="flex flex-wrap items-center gap-2 mt-1.5">
                              <span className="bg-primary-50 text-primary-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                                📍 {placementInfo?.label || ad.placement}
                              </span>
                              <span className="bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 rounded-full font-medium">
                                {adsSizes[ad.size]?.label || ad.size}
                              </span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ad.is_active && !isExpired ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                {ad.is_active && !isExpired ? '● Live' : '○ Inactive'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={() => setAdsForm({...ad, _newFile: null, _preview: null})} title="Edit"
                              className="p-2 rounded-lg hover:bg-gray-100 transition text-gray-400 hover:text-gray-700">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            </button>
                            <button onClick={async () => {
                              await adminAPI.toggleAd(ad.id);
                              const r = await adminAPI.getAds();
                              setAdsList(r.data.ads || []);
                              setAdsStats(r.data.stats || null);
                              showToast(ad.is_active ? 'Banner paused' : 'Banner activated');
                            }} title={ad.is_active ? 'Pause' : 'Activate'}
                              className="p-2 rounded-lg hover:bg-gray-100 transition text-gray-400 hover:text-gray-700">
                              {ad.is_active ?
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> :
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              }
                            </button>
                            <button onClick={async () => {
                              if (!window.confirm('Delete this banner?')) return;
                              await adminAPI.deleteAd(ad.id);
                              const r = await adminAPI.getAds();
                              setAdsList(r.data.ads || []);
                              setAdsStats(r.data.stats || null);
                              showToast('Banner deleted');
                            }} title="Delete"
                              className="p-2 rounded-lg hover:bg-red-50 transition text-gray-400 hover:text-red-600">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                        </div>
                        {/* Stats Row */}
                        <div className="flex items-center gap-5 mt-3 text-[12px] text-gray-500">
                          <span className="flex items-center gap-1">👁️ {Number(ad.view_count || 0).toLocaleString()} views</span>
                          <span className="flex items-center gap-1">🖱️ {Number(ad.click_count || 0).toLocaleString()} clicks</span>
                          <span className="flex items-center gap-1">📊 {ctr}% CTR</span>
                          {ad.link_url && <a href={ad.link_url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline truncate max-w-[200px]">🔗 {ad.link_url}</a>}
                        </div>
                        {/* Date info */}
                        <div className="flex items-center gap-4 mt-2 text-[11px] text-gray-400">
                          {ad.starts_at && <span>Starts: {new Date(ad.starts_at).toLocaleDateString()}</span>}
                          {ad.expires_at && <span className={isExpired ? 'text-red-500 font-medium' : ''}>Expires: {new Date(ad.expires_at).toLocaleDateString()}</span>}
                          <span>Order: {ad.sort_order}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Placement Guide */}
          <Card className="p-5">
            <h3 className="text-[14px] font-semibold text-gray-900 mb-3 flex items-center gap-2">📐 Banner Placement Guide</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {adsPlacements.map(p => (
                <div key={p.key} className="border border-gray-200 rounded-lg p-3 hover:border-primary-300 hover:bg-primary-50/30 transition">
                  <p className="text-[13px] font-medium text-gray-900">{p.label}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">{p.desc}</p>
                  <p className="text-[11px] text-primary-600 font-medium mt-1">Size: {p.size}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

            {/* ═══ SETTINGS ═══ */}
      {activeTab === 'settings' && !tabLoading && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[18px] font-semibold text-gray-900">Settings</h2>
              <p className="text-[13px] text-gray-500 mt-0.5">Configure your platform settings</p>
            </div>
            <button onClick={saveSettings} disabled={settingsSaving}
              className="px-4 py-2 bg-gray-900 text-white text-[13px] font-medium rounded-lg hover:bg-gray-800 transition disabled:opacity-50 flex items-center gap-2">
              {settingsSaving && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
              Save Changes
            </button>
          </div>

          {/* Settings sub-tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto">
            {[
              { key: 'branding', label: 'Branding', icon: 'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M18 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75zM15.75 9a.75.75 0 11-1.5 0 .75.75 0 011.5 0z' },
              { key: 'general', label: 'General', icon: 'M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75' },
              { key: 'seo', label: 'SEO', icon: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z' },
              { key: 'payment', label: 'Payment', icon: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z' },
              { key: 'listings', label: 'Listings', icon: 'M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z' },
              { key: 'notifications', label: 'Notifications', icon: 'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0' },
              { key: 'social', label: 'Social', icon: 'M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z' },
              { key: 'plans', label: 'Plans', icon: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z' },
              { key: 'security', label: 'Security', icon: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setSettingsTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium whitespace-nowrap transition
                  ${settingsTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} /></svg>
                {tab.label}
              </button>
            ))}
          </div>

          {settingsTab === 'branding' && (
            <Card className="p-6">
              <h3 className="text-[15px] font-semibold text-gray-900 mb-5">Branding & Media</h3>
              <div className="space-y-8">
                <div className="flex flex-col sm:flex-row gap-5">
                  <div className="sm:w-1/3">
                    <p className="text-[13px] font-medium text-gray-900">Site Logo</p>
                    <p className="text-xs text-gray-400 mt-1">Displayed in the navbar. Recommended: PNG/SVG, max height 40px.</p>
                  </div>
                  <div className="sm:flex-1 flex items-center gap-4">
                    <div className="w-32 h-16 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                      {settings.site_logo ? <img src={settings.site_logo} alt="Logo" className="max-h-14 max-w-full object-contain" /> : <span className="text-xs text-gray-400">No logo</span>}
                    </div>
                    <label className="cursor-pointer px-4 py-2 bg-white border border-gray-300 rounded-lg text-[13px] font-medium text-gray-700 hover:bg-gray-50 transition">
                      {brandingUploading ? 'Uploading...' : 'Upload Logo'}
                      <input type="file" accept="image/*" className="hidden" disabled={brandingUploading} onChange={(e) => handleBrandingUpload('site_logo', e.target.files[0])} />
                    </label>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-5">
                  <div className="sm:w-1/3">
                    <p className="text-[13px] font-medium text-gray-900">Favicon</p>
                    <p className="text-xs text-gray-400 mt-1">Browser tab icon. Recommended: Square PNG, 32x32 or 64x64px.</p>
                  </div>
                  <div className="sm:flex-1 flex items-center gap-4">
                    <div className="w-16 h-16 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                      {settings.site_favicon ? <img src={settings.site_favicon} alt="Favicon" className="w-8 h-8 object-contain" /> : <span className="text-xs text-gray-400">None</span>}
                    </div>
                    <label className="cursor-pointer px-4 py-2 bg-white border border-gray-300 rounded-lg text-[13px] font-medium text-gray-700 hover:bg-gray-50 transition">
                      {brandingUploading ? 'Uploading...' : 'Upload Favicon'}
                      <input type="file" accept="image/*" className="hidden" disabled={brandingUploading} onChange={(e) => handleBrandingUpload('site_favicon', e.target.files[0])} />
                    </label>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-5">
                  <div className="sm:w-1/3">
                    <p className="text-[13px] font-medium text-gray-900">Default Social Image (OG Image)</p>
                    <p className="text-xs text-gray-400 mt-1">Shown when sharing links on social media. Recommended: 1200x630px.</p>
                  </div>
                  <div className="sm:flex-1 flex items-center gap-4">
                    <div className="w-40 h-20 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                      {settings.og_image ? <img src={settings.og_image} alt="OG" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-gray-400">No image</span>}
                    </div>
                    <label className="cursor-pointer px-4 py-2 bg-white border border-gray-300 rounded-lg text-[13px] font-medium text-gray-700 hover:bg-gray-50 transition">
                      {brandingUploading ? 'Uploading...' : 'Upload OG Image'}
                      <input type="file" accept="image/*" className="hidden" disabled={brandingUploading} onChange={(e) => handleBrandingUpload('og_image', e.target.files[0])} />
                    </label>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Plans tab */}
          {settingsTab === 'plans' && planConfig && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-[15px] font-semibold text-gray-900">Subscription Plans</h3>
                  <p className="text-xs text-gray-400 mt-1">Configure limits and pricing for each plan. Changes apply immediately to new subscriptions.</p>
                </div>
                <button onClick={savePlans} disabled={plansSaving}
                  className="px-4 py-2 bg-gray-900 text-white text-[13px] font-medium rounded-lg hover:bg-gray-800 transition disabled:opacity-50 flex items-center gap-2">
                  {plansSaving && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
                  Save Plans
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {['free', 'basic', 'premium'].map(planKey => {
                  const plan = planConfig[planKey];
                  if (!plan) return null;
                  const icons = { free: '🆓', basic: '⭐', premium: '💎' };
                  const colors = { free: 'border-gray-200 bg-gray-50', basic: 'border-blue-200 bg-blue-50/30', premium: 'border-purple-200 bg-purple-50/30' };
                  const isFree = planKey === 'free';

                  return (
                    <div key={planKey} className={`border-2 rounded-xl p-5 ${colors[planKey]}`}>
                      <div className="text-center mb-4">
                        <span className="text-2xl">{icons[planKey]}</span>
                        <h4 className="text-[14px] font-bold text-gray-900 mt-1 uppercase">{planKey}</h4>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Label</label>
                          <input type="text" value={plan.label || ''} onChange={e => updatePlanField(planKey, 'label', e.target.value)}
                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-[13px] focus:ring-1 focus:ring-gray-400 focus:border-gray-400" />
                        </div>
                        <div>
                          <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Description</label>
                          <input type="text" value={plan.description || ''} onChange={e => updatePlanField(planKey, 'description', e.target.value)}
                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-[13px] focus:ring-1 focus:ring-gray-400 focus:border-gray-400" />
                        </div>

                        <div className="border-t border-gray-200 pt-3 mt-3">
                          <p className="text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2">Limits</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[11px] text-gray-500">Max Services</label>
                              <input type="number" min="0" value={plan.max_services} onChange={e => updatePlanField(planKey, 'max_services', e.target.value)}
                                className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-[13px] focus:ring-1 focus:ring-gray-400 focus:border-gray-400" />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500">Max Products</label>
                              <input type="number" min="0" value={plan.max_products} onChange={e => updatePlanField(planKey, 'max_products', e.target.value)}
                                className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-[13px] focus:ring-1 focus:ring-gray-400 focus:border-gray-400" />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500">Images/Listing</label>
                              <input type="number" min="1" max="20" value={plan.max_images} onChange={e => updatePlanField(planKey, 'max_images', e.target.value)}
                                className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-[13px] focus:ring-1 focus:ring-gray-400 focus:border-gray-400" />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500">Featured</label>
                              <input type="number" min="0" value={plan.featured_listings} onChange={e => updatePlanField(planKey, 'featured_listings', e.target.value)}
                                className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-[13px] focus:ring-1 focus:ring-gray-400 focus:border-gray-400" />
                            </div>
                          </div>
                        </div>

                        {!isFree && (
                          <div className="border-t border-gray-200 pt-3 mt-3">
                            <p className="text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2">Pricing (RWF)</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[11px] text-gray-500">Monthly</label>
                                <input type="number" min="0" step="500" value={plan.price_monthly} onChange={e => updatePlanField(planKey, 'price_monthly', e.target.value)}
                                  className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-[13px] focus:ring-1 focus:ring-gray-400 focus:border-gray-400" />
                              </div>
                              <div>
                                <label className="text-[11px] text-gray-500">Yearly</label>
                                <input type="number" min="0" step="1000" value={plan.price_yearly} onChange={e => updatePlanField(planKey, 'price_yearly', e.target.value)}
                                  className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-[13px] focus:ring-1 focus:ring-gray-400 focus:border-gray-400" />
                              </div>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1">Yearly savings: {plan.price_monthly * 12 > 0 ? Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100) : 0}%</p>
                          </div>
                        )}

                        {isFree && (
                          <div className="border-t border-gray-200 pt-3 mt-3">
                            <p className="text-[11px] text-gray-400 italic">Free plan — no pricing</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-[12px] text-yellow-700"><strong>Note:</strong> Changing limits here affects new subscriptions and limit checks immediately. Existing subscribers keep their stored limits until they renew. Use 999 for "unlimited".</p>
              </div>
            </Card>
          )}

          {/* Settings form - grouped */}
          {(() => {
            const settingGroups = {
              general: [
                { title: 'Site Identity', keys: ['site_name', 'site_description', 'site_tagline'] },
                { title: 'Contact Information', keys: ['contact_email', 'contact_phone', 'contact_address'] },
                { title: 'Regional Settings', keys: ['default_currency', 'default_language', 'timezone'] },
                { title: 'System', keys: ['maintenance_mode', 'enable_registration', 'require_email_verification', 'max_upload_size'] },
              ],
              seo: [
                { title: 'Meta Tags', keys: ['meta_title', 'meta_description', 'meta_keywords'] },
                { title: 'Analytics & Tracking', keys: ['google_analytics_id', 'google_tag_manager_id', 'facebook_pixel_id'] },
              ],
              payment: [
                { title: 'Paystack Integration', icon: '💳', keys: ['paystack_public_key', 'paystack_secret_key', 'paystack_webhook_secret'] },
                { title: 'Mobile Money', icon: '📱', keys: ['mtn_momo_number', 'airtel_money_number'] },
                { title: 'Commission & Withdrawal', icon: '💰', keys: ['commission_rate', 'min_withdrawal_amount'] },
              ],
              listings: [
                { title: 'Listing Limits', keys: ['max_free_listings', 'max_images_per_listing', 'listing_expiry_days'] },
                { title: 'Featured Listings', keys: ['featured_listing_price', 'featured_listing_duration'] },
                { title: 'Moderation', keys: ['require_listing_approval', 'auto_approve_verified'] },
              ],
              notifications: [
                { title: 'Email Notifications', keys: ['enable_email_notifications', 'enable_deals_email'] },
                { title: 'SMS', keys: ['enable_sms_notifications', 'sms_api_key', 'sms_api_url', 'sms_sender_id'] },
                { title: 'SMTP Configuration', icon: '📧', keys: ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_password', 'smtp_from_email', 'smtp_from_name'] },
              ],
              social: [
                { title: 'Social Media Links', keys: ['facebook_url', 'twitter_url', 'instagram_url', 'linkedin_url', 'youtube_url', 'whatsapp_url'] },
              ],
              security: [
                { title: 'Authentication', keys: ['enable_2fa', 'require_strong_password', 'max_login_attempts', 'session_timeout'] },
                { title: 'Verification', keys: ['require_provider_verification'] },
              ],
            };

            const groups = settingGroups[settingsTab] || [];
            const allGroupedKeys = groups.flatMap(g => g.keys);
            const ungrouped = Object.entries(settingsMeta)
              .filter(([key, m]) => m.category === settingsTab && !['site_logo', 'site_favicon', 'subscription_plans'].includes(key) && !allGroupedKeys.includes(key));

            const renderField = (key) => {
              const meta = settingsMeta[key];
              if (!meta) return null;
              const val = settings[key] || '';
              const isBoolean = meta.type === 'boolean';
              const isNumber = meta.type === 'number';
              const isSensitive = ['password', 'secret', 'api_key', 'client_secret'].some(w => key.includes(w));
              return (
                <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 py-3.5">
                  <div className="sm:w-2/5 flex-shrink-0">
                    <label className="text-[13px] font-medium text-gray-900">{meta.label}</label>
                    {meta.description && <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">{meta.description}</p>}
                  </div>
                  <div className="sm:flex-1">
                    {isBoolean ? (
                      <div className="flex items-center gap-3">
                        <button onClick={() => setSettings({ ...settings, [key]: val === 'true' ? 'false' : 'true' })}
                          className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${val === 'true' ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${val === 'true' ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                        </button>
                        <span className="text-[12px] text-gray-500">{val === 'true' ? 'Enabled' : 'Disabled'}</span>
                      </div>
                    ) : meta.type === 'text' ? (
                      <textarea value={val} onChange={e => setSettings({ ...settings, [key]: e.target.value })} rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-[13px] text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition resize-none bg-gray-50/50" />
                    ) : (
                      <input type={isSensitive ? 'password' : isNumber ? 'number' : 'text'}
                        value={val} onChange={e => setSettings({ ...settings, [key]: e.target.value })}
                        placeholder={meta.label}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-[13px] text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition bg-gray-50/50" />
                    )}
                  </div>
                </div>
              );
            };

            return (
              <>
                {groups.map((group, gi) => (
                  <Card key={gi} className="mb-4">
                    <div className="px-5 pt-4 pb-2 border-b border-gray-100">
                      <h4 className="text-[14px] font-semibold text-gray-900 flex items-center gap-2">
                        {group.icon && <span>{group.icon}</span>}
                        {group.title}
                      </h4>
                    </div>
                    <div className="px-5 divide-y divide-gray-50">
                      {group.keys.map(key => renderField(key))}
                    </div>
                  </Card>
                ))}
                {ungrouped.length > 0 && (
                  <Card className="mb-4">
                    <div className="px-5 pt-4 pb-2 border-b border-gray-100">
                      <h4 className="text-[14px] font-semibold text-gray-900">Other Settings</h4>
                    </div>
                    <div className="px-5 divide-y divide-gray-50">
                      {ungrouped.map(([key]) => renderField(key))}
                    </div>
                  </Card>
                )}
                <div className="flex justify-end">
                  <button onClick={saveSettings} disabled={settingsSaving}
                    className="px-5 py-2.5 bg-gray-900 text-white text-[13px] font-medium rounded-lg hover:bg-gray-800 transition disabled:opacity-50 flex items-center gap-2 shadow-sm">
                    {settingsSaving && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
                    Save Changes
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboardPage;
